/**
 * PlantillasManager.js - Administrador de plantillas de email
 * Permite ver, editar y previsualizar plantillas de correo
 */
import React, { useState, useEffect, useCallback } from 'react';

// Categorías de plantillas
const CATEGORIAS_PLANTILLAS = [
  { value: 'autenticacion', label: 'Autenticacion', descripcion: 'Bienvenida, verificacion, magic links' },
  { value: 'recordatorios', label: 'Recordatorios', descripcion: 'Vacunas, citas, medicamentos' },
  { value: 'veterinarios', label: 'Veterinarios', descripcion: 'Invitaciones, citas, acceso' },
  { value: 'recuperacion', label: 'Recuperacion', descripcion: 'Mascota/objeto encontrado' },
  { value: 'documentos', label: 'Documentos', descripcion: 'Vencimientos, compartidos' },
  { value: 'sistema', label: 'Sistema', descripcion: 'Confirmaciones, notificaciones' },
  { value: 'otros', label: 'Otros', descripcion: 'Sin categoria' }
];

// Datos de preview para las variables
const DATOS_PREVIEW = {
  nombrePropietario: 'Juan Perez',
  nombreMascota: 'Firulais',
  linkPanel: 'https://app.lemmontags.com/panel',
  linkVerificacion: 'https://app.lemmontags.com/verificar/abc123',
  nombreVeterinario: 'Dr. Garcia',
  fechaCita: '15 de enero de 2026',
  horaCita: '10:00 AM',
  motivoCita: 'Vacunacion anual',
  tituloRecordatorio: 'Vacuna antirrabica',
  fechaRecordatorio: '20 de enero de 2026',
  codigoActivacion: 'ABC123',
  folio: '12345',
  linkActivacion: 'https://app.lemmontags.com/activar?codigo=ABC123',
  nombreProveedor: 'Pet Shop Central',
  direccion: 'Av. Reforma 123, CDMX',
  telefono: '55 1234 5678',
  email: 'usuario@ejemplo.com'
};

const PlantillasManager = ({ addToHistory }) => {
  const [plantillas, setPlantillas] = useState([]);
  const [categoriaFiltro, setCategoriaFiltro] = useState('todas');
  const [plantillaEditando, setPlantillaEditando] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Estado del formulario de edicion
  const [form, setForm] = useState({
    asunto: '',
    contenido: '',
    variables: [],
    categoria: 'otros',
    descripcion: ''
  });

  // Cargar plantillas
  const cargarPlantillas = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken();

      const params = categoriaFiltro !== 'todas' ? `?categoria=${categoriaFiltro}` : '';
      const response = await fetch(`${API_URL}/api/admin/plantillas${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error cargando plantillas');
      }

      const data = await response.json();
      setPlantillas(data.data || []);
    } catch (err) {
      console.error('Error cargando plantillas:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [categoriaFiltro]);

  useEffect(() => {
    cargarPlantillas();
  }, [cargarPlantillas]);

  // Cargar plantilla para editar
  const editarPlantilla = async (id) => {
    setLoading(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken();

      const response = await fetch(`${API_URL}/api/admin/plantillas/${encodeURIComponent(id)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error cargando plantilla');
      }

      const data = await response.json();
      const plantilla = data.data;

      setForm({
        asunto: plantilla.asunto || '',
        contenido: plantilla.contenido || '',
        variables: plantilla.variables || [],
        categoria: plantilla.categoria || 'otros',
        descripcion: plantilla.descripcion || ''
      });
      setPlantillaEditando(plantilla);
      addToHistory?.(`Editando plantilla: ${id}`);
    } catch (err) {
      console.error('Error cargando plantilla:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Guardar plantilla
  const guardarPlantilla = async () => {
    if (!plantillaEditando) return;

    setSaving(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || '';
      const { getAuth } = await import('firebase/auth');
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken();

      const response = await fetch(`${API_URL}/api/admin/plantillas/${encodeURIComponent(plantillaEditando.id)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          asunto: form.asunto,
          contenido: form.contenido,
          variables: form.variables,
          categoria: form.categoria,
          descripcion: form.descripcion
        })
      });

      if (!response.ok) {
        throw new Error('Error guardando plantilla');
      }

      addToHistory?.(`Plantilla actualizada: ${plantillaEditando.id}`);
      setPlantillaEditando(null);
      cargarPlantillas();
    } catch (err) {
      console.error('Error guardando plantilla:', err);
      alert('Error guardando plantilla: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Obtener HTML con preview
  const getPreviewHtml = (contenido) => {
    if (!contenido) return '';
    let html = contenido;
    Object.entries(DATOS_PREVIEW).forEach(([key, value]) => {
      html = html.replace(new RegExp(`{{${key}}}`, 'g'), value);
    });
    // Marcar variables no reemplazadas
    html = html.replace(/{{(\w+)}}/g, '<span style="background:yellow;padding:2px 4px;">{{$1}}</span>');
    return html;
  };

  // Obtener label de categoria
  const getCategoriaLabel = (value) => {
    const cat = CATEGORIAS_PLANTILLAS.find(c => c.value === value);
    return cat ? cat.label : value;
  };

  // Formatear fecha
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Sin fecha';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp._seconds * 1000);
      return date.toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Sin fecha';
    }
  };

  // Filtrar plantillas
  const plantillasFiltradas = plantillas;

  return (
    <div className="plantillas-manager">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">
          <i className="bi bi-envelope-paper me-2"></i>
          Plantillas de Email
        </h4>
        <button
          className="btn btn-outline-primary btn-sm"
          onClick={cargarPlantillas}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Recargar
        </button>
      </div>

      {/* Filtro por categoria */}
      <div className="card mb-4">
        <div className="card-body py-2">
          <div className="row align-items-center">
            <div className="col-auto">
              <label className="form-label mb-0 fw-bold">Categoria:</label>
            </div>
            <div className="col-auto">
              <select
                className="form-select form-select-sm"
                value={categoriaFiltro}
                onChange={(e) => setCategoriaFiltro(e.target.value)}
              >
                <option value="todas">Todas las categorias</option>
                {CATEGORIAS_PLANTILLAS.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label} - {cat.descripcion}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-auto text-muted small">
              {plantillasFiltradas.length} plantilla(s)
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
        </div>
      )}

      {/* Loading */}
      {loading && !plantillaEditando && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2 text-muted">Cargando plantillas...</p>
        </div>
      )}

      {/* Tabla de plantillas */}
      {!loading && !plantillaEditando && (
        <div className="table-responsive">
          <table className="table table-hover">
            <thead className="table-light">
              <tr>
                <th>ID / Nombre</th>
                <th>Descripcion</th>
                <th>Categoria</th>
                <th>Asunto</th>
                <th>Modificado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {plantillasFiltradas.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-muted">
                    No hay plantillas en esta categoria
                  </td>
                </tr>
              ) : (
                plantillasFiltradas.map(p => (
                  <tr key={p.id}>
                    <td>
                      <code className="text-primary">{p.id}</code>
                    </td>
                    <td>
                      {p.descripcion || <span className="text-muted fst-italic">Sin descripcion</span>}
                    </td>
                    <td>
                      <span className={`badge bg-${getCategoriaColor(p.categoria)}`}>
                        {getCategoriaLabel(p.categoria)}
                      </span>
                    </td>
                    <td className="text-truncate" style={{ maxWidth: '200px' }}>
                      {p.asunto || <span className="text-muted">-</span>}
                    </td>
                    <td className="small text-muted">
                      {formatDate(p.updatedAt)}
                      {p.updatedBy && <div className="small">{p.updatedBy}</div>}
                    </td>
                    <td>
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => editarPlantilla(p.id)}
                      >
                        <i className="bi bi-pencil me-1"></i>
                        Editar
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de edicion */}
      {plantillaEditando && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-xl modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="bi bi-pencil-square me-2"></i>
                  Editar plantilla: <code>{plantillaEditando.id}</code>
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setPlantillaEditando(null)}
                  disabled={saving}
                />
              </div>
              <div className="modal-body">
                <div className="row">
                  {/* Columna izquierda - Formulario */}
                  <div className="col-md-6">
                    {/* Categoria */}
                    <div className="mb-3">
                      <label className="form-label fw-bold">Categoria</label>
                      <select
                        className="form-select"
                        value={form.categoria}
                        onChange={(e) => setForm({ ...form, categoria: e.target.value })}
                      >
                        {CATEGORIAS_PLANTILLAS.map(cat => (
                          <option key={cat.value} value={cat.value}>
                            {cat.label} - {cat.descripcion}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Descripcion */}
                    <div className="mb-3">
                      <label className="form-label fw-bold">Descripcion (uso interno)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.descripcion}
                        onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
                        placeholder="Cuando se envia esta plantilla?"
                      />
                    </div>

                    {/* Asunto */}
                    <div className="mb-3">
                      <label className="form-label fw-bold">Asunto del correo</label>
                      <input
                        type="text"
                        className="form-control"
                        value={form.asunto}
                        onChange={(e) => setForm({ ...form, asunto: e.target.value })}
                      />
                    </div>

                    {/* Variables */}
                    <div className="mb-3">
                      <label className="form-label fw-bold">Variables disponibles</label>
                      <div className="d-flex flex-wrap gap-1 mb-2">
                        {form.variables?.map(v => (
                          <code key={v} className="badge bg-light text-dark border">{`{{${v}}}`}</code>
                        ))}
                        {(!form.variables || form.variables.length === 0) && (
                          <span className="text-muted small">Sin variables definidas</span>
                        )}
                      </div>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={form.variables?.join(', ') || ''}
                        onChange={(e) => setForm({
                          ...form,
                          variables: e.target.value.split(',').map(v => v.trim()).filter(v => v)
                        })}
                        placeholder="variable1, variable2, variable3"
                      />
                      <small className="text-muted">
                        Separadas por comas. Edita si agregas/quitas variables del contenido.
                      </small>
                    </div>

                    {/* Contenido HTML */}
                    <div className="mb-3">
                      <label className="form-label fw-bold">Contenido HTML</label>
                      <textarea
                        rows={15}
                        className="form-control font-monospace"
                        value={form.contenido}
                        onChange={(e) => setForm({ ...form, contenido: e.target.value })}
                        style={{ fontSize: '12px' }}
                      />
                    </div>
                  </div>

                  {/* Columna derecha - Preview */}
                  <div className="col-md-6">
                    <label className="form-label fw-bold">Vista previa</label>
                    <div
                      className="border rounded p-3 bg-white"
                      style={{
                        height: 'calc(100% - 30px)',
                        overflow: 'auto',
                        minHeight: '500px'
                      }}
                      dangerouslySetInnerHTML={{ __html: getPreviewHtml(form.contenido) }}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setPlantillaEditando(null)}
                  disabled={saving}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={guardarPlantilla}
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-1"></span>
                      Guardando...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-check-lg me-1"></i>
                      Guardar cambios
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper para color de categoria
function getCategoriaColor(categoria) {
  const colores = {
    autenticacion: 'primary',
    recordatorios: 'warning',
    veterinarios: 'success',
    recuperacion: 'danger',
    documentos: 'info',
    sistema: 'secondary',
    otros: 'dark'
  };
  return colores[categoria] || 'secondary';
}

export default PlantillasManager;
